import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuthState } from '@/hooks/useAuth'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { syncDeviceNotificationStateToFirestore } from '@/lib/notifications'

interface NotificationSettingsState {
  iosEnabled: boolean
  androidEnabled: boolean
  needsSetup: boolean
  isLoading: boolean
}

export function useNotificationSettingsListener(): NotificationSettingsState {
  const { user } = useAuthState()
  const [state, setState] = useState<NotificationSettingsState>({
    iosEnabled: false,
    androidEnabled: false,
    needsSetup: true,
    isLoading: true,
  })

  useEffect(() => {
    if (!user?.uid) {
      setState({ iosEnabled: false, androidEnabled: false, needsSetup: true, isLoading: true })
      return
    }

    if (Capacitor.isNativePlatform()) {
      const p = Capacitor.getPlatform()
      if (p === 'ios' || p === 'android') {
        syncDeviceNotificationStateToFirestore(user.uid).catch((error) => {
          console.error('[useNotificationSettingsListener] Error syncing device state:', error)
        })
      }
    }

    const ref = doc(db, 'users', user.uid)
    const unsub = onSnapshot(ref, async (snap) => {
      const data = snap.data() as any
      const firestoreIosEnabled = !!data?.notificationSettings?.ios?.enabled
      const firestoreAndroidEnabled = !!data?.notificationSettings?.android?.enabled

      let actualIosEnabled = firestoreIosEnabled
      if (firestoreIosEnabled && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        try {
          const permStatus = await PushNotifications.checkPermissions()
          actualIosEnabled = firestoreIosEnabled && permStatus.receive === 'granted'
        } catch (error) {
          console.error('[useNotificationSettingsListener] Error checking native iOS permissions:', error)
          actualIosEnabled = firestoreIosEnabled
        }
      }

      let actualAndroidEnabled = firestoreAndroidEnabled
      if (
        firestoreAndroidEnabled &&
        Capacitor.isNativePlatform() &&
        Capacitor.getPlatform() === 'android'
      ) {
        try {
          const permStatus = await PushNotifications.checkPermissions()
          actualAndroidEnabled = firestoreAndroidEnabled && permStatus.receive === 'granted'
        } catch (error) {
          console.error('[useNotificationSettingsListener] Error checking native Android permissions:', error)
          actualAndroidEnabled = firestoreAndroidEnabled
        }
      }

      const p = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web'
      let needsSetup: boolean
      if (p === 'android') {
        needsSetup = !actualAndroidEnabled
      } else if (p === 'ios') {
        needsSetup = !actualIosEnabled
      } else {
        needsSetup = !actualIosEnabled && !actualAndroidEnabled
      }

      setState({
        iosEnabled: actualIosEnabled,
        androidEnabled: actualAndroidEnabled,
        needsSetup,
        isLoading: false,
      })
    })

    return () => unsub()
  }, [user?.uid])

  return state
}
